import React, {createContext, useEffect, useMemo, useReducer} from 'react';
import PropTypes from 'prop-types';
import DOMUtils from '@spothero/utils/dom';
import AnimationUtils from '@spothero/utils/animation';

const ENQUEUE = 'ENQUEUE';
const DEQUEUE = 'DEQUEUE';
const initialState = [];

const reducer = (state = initialState, {type, payload}) => {
    switch (type) {
        case ENQUEUE:
            return state.concat(payload);
        case DEQUEUE:
            // either remove all items in array that match payload, or the first item
            return payload
                ? state.filter((item) => item.selector !== payload)
                : state.slice(1);
        default:
            return state;
    }
};

/**
 * Manages the scroll state.  To be used with the {@link AutoScrollProvider} component only.
 * @private
 *
 * @returns {{addToScrollQueue: TAddToScrollQueue, removeFromQueue: TRemoveFromQueue, scrollQueue: Array<string>}}
 *
 * @memberof module:hooks/autoScroll
 */
const useAutoScrollState = () => {
    const [queue, dispatch] = useReducer(reducer, initialState);
    const createAction = (type) => (payload) => dispatch({type, payload});
    /**  @type {TRemoveFromQueue} */
    const removeFromQueue = useMemo(() => createAction(DEQUEUE), []);
    /** @type {TAddToScrollQueue} */
    const addToScrollQueue = useMemo(() => createAction(ENQUEUE), []);

    // this is the maintainer of the queue
    useEffect(() => {
        if (queue.length) {
            // TODO update useAutoScroll for component ref support
            const {selector, duration, onComplete, parentContainer} = queue[0];
            let element = DOMUtils.el(selector);

            element = element[0] ?? element;

            AnimationUtils.scrollTo(
                parentContainer || 'html',
                element.getBoundingClientRect().y,
                duration || 1000,
                'y',
                () => {
                    if (onComplete) {
                        onComplete();
                    }
                    removeFromQueue();
                }
            );
        }
    }, [queue, removeFromQueue]);

    return {
        removeFromQueue,
        addToScrollQueue,
        scrollQueue: queue,
    };
};

/**
 * Context for the AutoScrollProvider
 * @type {React.Context<TAutoScrollContext>}
 *
 * @memberof module:hooks/autoScroll
 */
export const AutoScrollContext = createContext();

/**
 * Provider for the AutoScrollContext.  To only be used within App.jsx since having more than one of these providers could cause scroll animation bubbling issues.
 * @function AutoScrollProvider
 * @param {JSX.Element|Array<JSX.Element>} children
 * @return {JSX.Element}
 *
 * @memberof module:hooks/autoScroll
 */
export const AutoScrollProvider = ({children}) => {
    const {removeFromQueue, addToScrollQueue, scrollQueue} =
        useAutoScrollState();

    return (
        <AutoScrollContext.Provider
            value={{
                removeFromQueue,
                addToScrollQueue,
                scrollQueue,
            }}
        >
            {children}
        </AutoScrollContext.Provider>
    );
};

AutoScrollProvider.propTypes = {
    children: PropTypes.node.isRequired,
};
